import { Box, Card, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Contract_Status_Enum, Duration_Type_Enum, RecommendationPageContext } from '@kheops/graphql';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging } from '@kheops/utils';
import i18n from '../../i18n';
import { RoutePaths } from '../../routes/AppRoutes';
import { ContractPreview } from '../../hooks/useContracts';
import { PageWrapper } from '../../common/components/PageWrapper';
import ExpandableText from '../../common/components/ExpandableText';
import TopAppBar from '../../common/components/TopAppBar';
import FileUtils from '../../common/utils/file.utils';
import { contractsAtom, currentContextAtom } from '../../state';
import { ProductVersionByIdQuery, useProductVersionByIdQuery } from '../../queries/__generated__/productVersionById.generated';
import QualityLabels from '../../search/product/QualityLabels';
import { currentBuyingCompanyIdAtom } from '../../basket/state/state';
import useRelevantSocialTags from '../../social-proof/hooks/useRelevantSocialTags';
import ProductNutritionFacts from '../productPage/Product/ProductNutritionFacts';
import BillingTypeCard from '../BillingTypeCard';
import BaseUnitPhotosDisplay from '../BaseUnitPhotosDisplay';
import UpdateBasketButton from '../../search/product/UpdateBasketButton';
import SocialTagCard from '../../social-proof/SocialTagCard';
import { InfoBoxSx } from '../../common/components/InfoBox';
import WishlistProductButton from '../../wishlist/WishlistProductButton';
import ProductRecommendations from '../recommendations/ProductRecommendations';
import PackagingPageBreadcrumb from './PackagingPageBreadcrumb';
import PackagingPagePrices from './PackagingPagePrices';
import PackagingPageSelects from './PackagingPageSelects';
import PackagingPageSpecifications from './PackagingPageSpecifications';
import PackagingPageHeader from './PackagingPageHeader';
import PackagingPreservationInfo from './PackagingPreservationInfo';

// Extract type of packaging from the query ProductVersionByIdQuery
// Using `Array<>[number]` let's us get a type from a value in an array.
export type Product = Required<ProductVersionByIdQuery>['product'][number] | Required<ProductVersionByIdQuery>['product_history'][number];
export type BaseUnit = Omit<NonNullable<ProductVersionByIdQuery['product']>[number]['base_units'][number], '__typename'>;
export type Packaging = Omit<BaseUnit['packagings'][number], '__typename'>;

export default function PackagingPage(): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['products', 'product-classification-tags']);
  const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const { productSku, companyId, productId } = useParams();
  const [searchParams] = useSearchParams();
  const context = useAtomValue(currentContextAtom);
  const contracts = useAtomValue(contractsAtom);
  const buyingCompanyId = useAtomValue(currentBuyingCompanyIdAtom);
  const { data: productData, loading } = useProductVersionByIdQuery({
    variables: {
      companyId,
      productSku: Number(productSku),
      productId,
      shouldCheckHistory: !!productId,
    },
  });
  const [baseUnit, setBaseUnit] = useState<BaseUnit>();
  const [packaging, setPackaging] = useState<Packaging>();
  const socialTags = useRelevantSocialTags(baseUnit?.social_tags || [], { rules: { 1: 1, 2: 2 }, max: 2 });
  const supplierLink = generatePath(RoutePaths.CONTRACT_COMPANY, { companyId });

  const isBuyer = useMemo(
    () => context.realm === Business_Profile_Enum.Buyer,
    [context],
  );

  const contract = useMemo((): ContractPreview | undefined => {
    return contracts.find(({ supplyingCompanyId, buyingCompanyId: contractBuyingCompanyId }) => {
      return supplyingCompanyId === companyId && contractBuyingCompanyId === buyingCompanyId;
    });
  }, [contracts, companyId, isBuyer]);

  const product = useMemo(() => {
    if (productData?.product) {
      return productData.product[0];
    }
    if (productData?.product_history) {
      return productData.product_history[0];
    }
  }, [productData]);
  const classificationTags = productData?.product?.[0]?.classification_tags;
  const showPreservationAndLabels = [
    product?.preservation_method,
    product?.shelf_life_duration,
    product?.shelf_life_duration_type,
    product?.labels?.length,
  ].some(Boolean);

  useEffect((): void => {
    if (product) {
      const csuSearchParam = searchParams.get('csu') || '';
      const packagingSearchParam = searchParams.get('packaging') || '';

      const baseUnitResult = product.base_units.find(({ sku, id }) => sku === Number(csuSearchParam) || id === csuSearchParam);
      const packagingResult = baseUnitResult?.packagings.find(({ sku, id }) => sku === Number(packagingSearchParam) || id === packagingSearchParam);

      let adjustedPackaging = (packagingResult || baseUnitResult?.packagings[0])!;

      if (contract && isBuyer) {
        const customPriceListDiscount = contract.custom_price_list?.custom_price_list_packagings
          .find(({ packaging_sku }) => adjustedPackaging.sku === packaging_sku)
          ?.discount;
        const discount = customPriceListDiscount || contract.discount;

        if (discount) {
          adjustedPackaging = {
            ...adjustedPackaging,
            price: adjustedPackaging.price * (1 - discount),
          };
        }
      }

      setBaseUnit((baseUnitResult || product.base_units[0]) as BaseUnit);
      setPackaging(adjustedPackaging);
    }
  }, [product, searchParams]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!loading && product && baseUnit && packaging && (
        <>
          <TopAppBar
            configuration="small"
            backTo={contract ? supplierLink : (isBuyer ? RoutePaths.DISCOVERY_SUPPLIERS : RoutePaths.DISCOVERY_BUYERS)}
            title={product.name}
          />
          <PageWrapper
            sx={{
              pt: {
                xs: 0.5,
                lg: 2,
              },
            }}
          >
            <Box
              sx={{
                display: {
                  xs: 'none',
                  lg: 'inline-flex',
                },
                alignItems: 'center',
                mb: 2,
              }}
            >
              <PackagingPageBreadcrumb contract={contract} product={product} />
            </Box>
            <Card
              sx={{
                p: {
                  xs: 2,
                  xl: 3,
                },
              }}
            >
              {isBuyer && contract?.status !== Contract_Status_Enum.Signed && <PackagingPageHeader product={product} contract={contract} />}
              <Grid container columnSpacing={2} rowSpacing={2} sx={{ pt: 0.5 }}>
                <Grid size={{ xs: 12, lg: 7 }}>
                  <BaseUnitPhotosDisplay
                    mainPhoto={baseUnit.main_photo}
                    photos={baseUnit.photos.map((photoData) => photoData.photo)}
                    productSubFamily={product.sub_family}
                    overlay={
                      contract?.status !== Contract_Status_Enum.Signed
                        ? (
                          <WishlistProductButton
                            supplierId={companyId!}
                            productSku={product.sku!}
                          />
                        )
                        : undefined
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }} sx={{ pl: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'start',
                      gap: 0.5,
                    }}
                  >
                    {!isMobile && (
                      <Typography variant="headlineSmall">
                        {product.name}
                      </Typography>
                    )}
                    <Typography variant="bodyLarge" color="onSurfaceVariant">
                      {FormatBaseUnitQuantity(baseUnit!, 'fr', numberFormat)} - {FormatLogisticalPackaging(packaging!, 'fr', baseUnit)}
                    </Typography>
                  </Box>
                  {showPreservationAndLabels && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        my: 1.5,
                      }}
                    >
                      <PackagingPreservationInfo
                        preservationMethod={product.preservation_method}
                        shelfLifeDurationType={product.shelf_life_duration_type as Duration_Type_Enum}
                        shelfLifeDuration={product.shelf_life_duration}
                      />
                      {!!product.labels?.length && (
                        <QualityLabels
                          labels={product.labels.map(({ label }) => label)}
                          nbToDisplay={10}
                          size={32}
                          iconSx={InfoBoxSx}
                        />
                      )}
                    </Box>
                  )}
                  {!!classificationTags?.length && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: !showPreservationAndLabels ? 1.5 : 0, mb: 3 }}>
                      {classificationTags.map(({ tag_code }) => (
                        <Chip
                          key={tag_code}
                          variant="outlinedTag"
                          label={t(`product-classification-tags:${tag_code}`)}
                        />
                      ))}
                    </Box>
                  )}
                  {contract && (
                    <Box
                      component={Link}
                      to={supplierLink}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 1.5,
                        color: 'onSurface.main',
                      }}
                    >
                      <Box
                        component="img"
                        src={FileUtils.BuildCompanyImageUrl({ photo: product.company.photos[0]?.photo, type: 'SUPPLIER', size: { height: 24, width: 24 } })}
                        sx={{ mr: 1, height: 24, width: 24, borderRadius: '100%' }}
                      />
                      <Typography variant="bodyMedium">{product.company.tradeName}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                    <PackagingPagePrices baseUnit={baseUnit} packaging={packaging} vatRate={product.vat_rate!} />
                    <PackagingPageSelects product={product} />
                    {contract?.status === Contract_Status_Enum.Signed && (
                      <UpdateBasketButton
                        packagingId={packaging.id}
                        supplyingCompanyId={contract.supplyingCompanyId}
                        shouldDisplayTitle
                      />
                    )}
                    {!!socialTags.length && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          '& .MuiCard-root': {
                            flex: 1,
                          },
                        }}
                      >
                        {socialTags.map((tag) => (
                          <SocialTagCard tag={tag} key={tag.type} entity="BASE_UNIT" />
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {product.description && (
                        <>
                          <Typography variant="titleMedium">
                            {t('products:about_this_product')}
                          </Typography>
                          <ExpandableText
                            text={product.description}
                            lineClampThreshold={3}
                          />
                        </>
                      )}
                      <BillingTypeCard billingType={baseUnit.billing_type} />
                    </Box>
                    <PackagingPageSpecifications product={product} baseUnit={baseUnit} packaging={packaging} />
                    <ProductNutritionFacts product={product} />
                  </Box>
                </Grid>
              </Grid>
            </Card>
            {isBuyer && (
              <ProductRecommendations
                supplierId={companyId}
                pageContext={RecommendationPageContext.Packaging}
                subFamily={product.sub_family}
              />
            )}
          </PageWrapper>
        </>
      )}
    </>
  );
}
